import React, { useState } from 'react';
import { DvprItem, TestStatus } from '../types';
import { Plus, Search, Filter, BrainCircuit, Download } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';

interface DvprManagerProps {
  items: DvprItem[];
  setItems: React.Dispatch<React.SetStateAction<DvprItem[]>>;
}

export const DvprManager: React.FC<DvprManagerProps> = ({ items, setItems }) => {
  const { logTestResult, createDvprTest, exportProject, runDvprAiAnalysis } = useAppContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiInsight, setAiInsight] = useState('');
  const [isLoadingAi, setIsLoadingAi] = useState(false);

  const handleStatusChange = (id: string, newStatus: TestStatus) => {
    setItems(prev => prev.map(item => item.id === id ? { ...item, status: newStatus } : item));

    // Log result to backend if it's a final state
    if (newStatus === TestStatus.COMPLETED || newStatus === TestStatus.FAILED) {
      logTestResult(id, newStatus === TestStatus.COMPLETED ? 'PASS' : 'FAIL', undefined, undefined, 'Status updated via DVPR Manager');
    }
  };

  const filteredItems = items.filter(item =>
    item.testMethod.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.requirement.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddTest = async () => {
    await createDvprTest({
      requirement: "New Requirement",
      testMethod: "TBD",
      sampleSize: 1,
      status: TestStatus.PLANNED,
      comments: "Auto-generated from DVPR Manager"
    });
  };

  const handleExport = async () => {
    const url = await exportProject('pdf');
    window.open(url, '_blank');
  };

  const handleRunAiAnalysis = async () => {
    setIsLoadingAi(true);
    setShowAiModal(true);
    const insight = await runDvprAiAnalysis();
    setAiInsight(insight);
    setIsLoadingAi(false);
  };

  return (
    <div className="space-y-6 h-full flex flex-col">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Design Verification Plan & Report</h2>
          <p className="text-sm text-slate-500">Manage validation activities and track compliance.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRunAiAnalysis}
            className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 hover:bg-purple-100 rounded-lg text-sm font-medium transition-colors border border-purple-200"
          >
            <BrainCircuit size={16} />
            AI Risk Analysis
          </button>
          <button
            onClick={handleAddTest}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg text-sm font-medium transition-colors shadow-sm shadow-indigo-200"
          >
            <Plus size={16} />
            Add Test
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex gap-3 bg-white p-2 rounded-lg border border-slate-200 shadow-sm">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search requirements or tests..."
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border-transparent rounded-md text-sm focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button
          onClick={() => console.log('🔍 Filter click registered')}
          className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md"
        >
          <Filter size={18} />
        </button>
        <button
          onClick={handleExport}
          className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md"
        >
          <Download size={18} />
        </button>
      </div>

      {/* Data Grid */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm flex-1 overflow-hidden flex flex-col">
        <div className="overflow-x-auto custom-scrollbar flex-1">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 w-24">ID</th>
                <th className="px-6 py-3">Requirement</th>
                <th className="px-6 py-3">Test Method</th>
                <th className="px-6 py-3 w-32">Sample Size</th>
                <th className="px-6 py-3 w-32">Failures</th>
                <th className="px-6 py-3 w-40">Status</th>
                <th className="px-6 py-3">Comments</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredItems.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50 transition-colors group">
                  <td className="px-6 py-4 font-mono text-xs text-slate-500">{item.id}</td>
                  <td className="px-6 py-4 max-w-xs truncate text-slate-700 font-medium" title={item.requirement}>
                    {item.requirement}
                  </td>
                  <td className="px-6 py-4 text-slate-600">{item.testMethod}</td>
                  <td className="px-6 py-4 text-slate-600">{item.sampleSize}</td>
                  <td className={`px-6 py-4 font-medium ${item.failures > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                    {item.failures}
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={item.status}
                      onChange={(e) => handleStatusChange(item.id, e.target.value as TestStatus)}
                      className={`text-xs font-semibold px-2 py-1 rounded-full border-0 cursor-pointer outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500
                        ${item.status === TestStatus.COMPLETED ? 'bg-emerald-100 text-emerald-700' : ''}
                        ${item.status === TestStatus.IN_PROGRESS ? 'bg-blue-100 text-blue-700' : ''}
                        ${item.status === TestStatus.PLANNED ? 'bg-slate-100 text-slate-600' : ''}
                        ${item.status === TestStatus.FAILED ? 'bg-red-100 text-red-700' : ''}
                      `}
                    >
                      {Object.values(TestStatus).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-6 py-4 text-slate-500 max-w-xs truncate">{item.comments}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-slate-200 bg-slate-50 text-xs text-slate-500 flex justify-between items-center">
          <span>Showing {filteredItems.length} of {items.length} records</span>
          <div className="flex gap-2">
            <button
              onClick={() => console.log('Previous Page')}
              className="px-3 py-1 bg-white border border-slate-300 rounded hover:bg-slate-50"
            >
              Previous
            </button>
            <button
              onClick={() => console.log('Next Page')}
              className="px-3 py-1 bg-white border border-slate-300 rounded hover:bg-slate-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* AI Modal Overlay */}
      {showAiModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col animate-fade-in-up">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <BrainCircuit className="text-purple-600" />
                AI Failure Analysis (FMEA)
              </h3>
              <button onClick={() => setShowAiModal(false)} className="text-slate-400 hover:text-slate-600">
                Close
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 text-slate-700 leading-relaxed">
              {isLoadingAi ? (
                <div className="flex flex-col items-center justify-center py-12 gap-4">
                  <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-sm text-slate-500">Analyzing failure modes with Gemini 2.5 Flash...</p>
                </div>
              ) : (
                <div className="prose prose-sm prose-slate max-w-none">
                  {/* Rendering simple markdown-like text */}
                  {aiInsight.split('\n').map((line, idx) => (
                    <p key={idx} className={line.startsWith('-') ? 'ml-4' : 'mb-2'}>{line}</p>
                  ))}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-slate-100 bg-slate-50 rounded-b-xl flex justify-end">
              <button
                onClick={() => setShowAiModal(false)}
                className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};